import { apiSlice } from "./apiSlice";
import {
  getCoManufacturingOrder,
  getCoManufacturingOrderDetail,
  getCoManufacturingOrderSelection,
} from "../slice/manufacturingOrderSlice";
import {
  getManufacturingOrder as getManufacturingOrderAction,
  getManufacturingOrderSelection as getManufacturingOrderSelectionAction,
  getManufacturingOrderData,
} from "../slice/productionSlice";

export const manufacturingOrderApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    createManufacturingOrder: builder.mutation({
      query: data => ({
        url: "/api/production/v1/manufacturing_order/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["moList"],
    }),
    updateManufacturingOrder: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/production/v1/manufacturing_order/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["moList", { type: "moDetail", id }, "moSteps"],
    }),
    patchManufacturingOrder: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/production/v1/manufacturing_order/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["moList", { type: "moDetail", id }, "moSteps"],
    }),
    releaseManufacturingOrder: builder.mutation({
      query: ({ id }) => ({
        url: `/api/production/v1/manufacturing_order/${id}/release/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id }) => ["moList", { type: "moDetail", id }, "moSteps"],
    }),
    expandBom: builder.mutation({
      query: data => ({
        url: "/api/production/v1/manufacturing_order/expand_bom/",
        method: "POST",
        body: data,
      }),
    }),
    expandBomSteps: builder.mutation({
      query: data => ({
        url: "/api/production/v1/manufacturing_order/expand_bom_steps/",
        method: "POST",
        body: data,
      }),
    }),
    getManufacturingOrder: builder.query({
      query: (params = null) =>
        params
          ? `/api/production/v1/manufacturing_order/?${params}`
          : "/api/production/v1/manufacturing_order/",

      providesTags: ["moList"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(getManufacturingOrderAction(data));
        } catch (err) {}
      },
    }),
    getManufacturingOrderSelection: builder.query({
      query: params => ({
        url: `/api/production/v1/manufacturing_order/selection/${params || ""}`,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(getManufacturingOrderSelectionAction(data));
        } catch (err) {}
      },
    }),
    deleteManufacturingOrder: builder.mutation({
      query: id => ({
        url: `/api/production/v1/manufacturing_order/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["moList"],
    }),
    getManufacturingOrderDetail: builder.query({
      query: id => `/api/production/v1/manufacturing_order/${id}/`,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(getManufacturingOrderData(data));
        } catch (err) {}
      },
      providesTags: (result, error, id) => [{ type: "moDetail", id }],
    }),
    getManufacturingOrderHistory: builder.query({
      query: ({ id, limit = 20, offset = 0 }) => ({
        url: `/api/production/v1/manufacturing_order/${id}/history/`,
        params: { limit, offset },
      }),
    }),
    getMoSteps: builder.query({
      query: params => {
        const queryParams = new URLSearchParams(params).toString();
        return `/api/production/v1/mo_step/?${queryParams}`;
      },
      providesTags: ["moSteps"],
    }),
    getMoComponents: builder.query({
      query: params => {
        const queryParams = new URLSearchParams(params).toString();
        return `/api/production/v1/mo_component/?${queryParams}`;
      },
      providesTags: ["moSteps"],
    }),
    getMoLabours: builder.query({
      query: params => {
        const queryParams = new URLSearchParams(params).toString();
        return `/api/production/v1/mo_labour/?${queryParams}`;
      },
      providesTags: ["moSteps"],
    }),
    getMoSources: builder.query({
      query: params => {
        const queryParams = new URLSearchParams(params).toString();
        return `/api/production/v1/mo_source/?${queryParams}`;
      },
      providesTags: ["moSteps"],
    }),
    createCoManufacturingOrder: builder.mutation({
      query: data => ({
        url: "/api/production/v1/co_manufacturing_order/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["cmoList"],
    }),
    updateCoManufacturingOrder: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/production/v1/co_manufacturing_order/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["cmoList", { type: "cmoDetail", id }],
    }),
    patchCoManufacturingOrder: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/production/v1/co_manufacturing_order/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["cmoList", { type: "cmoDetail", id }],
    }),
    submitCoManufacturingOrder: builder.mutation({
      query: id => ({
        url: `/api/production/v1/co_manufacturing_order/${id}/submit/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => ["cmoList", { type: "cmoDetail", id }],
    }),
    deleteCoManufacturingOrder: builder.mutation({
      query: id => ({
        url: `/api/production/v1/co_manufacturing_order/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["cmoList"],
    }),
    getCoManufacturingOrder: builder.query({
      query: (params = null) =>
        params
          ? `/api/production/v1/co_manufacturing_order/?${params}`
          : "/api/production/v1/co_manufacturing_order/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getCoManufacturingOrder(result.data)))
          .catch(() => {});
      },
      providesTags: ["cmoList"],
    }),
    getCoManufacturingOrderSelection: builder.query({
      query: params => ({
        url: `/api/production/v1/co_manufacturing_order/selection/${params || ""}`,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getCoManufacturingOrderSelection(result.data)))
          .catch(() => {});
      },
    }),
    getCoManufacturingOrderDetail: builder.query({
      query: id => `/api/production/v1/co_manufacturing_order/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getCoManufacturingOrderDetail(result.data)))
          .catch(() => {});
      },
      providesTags: (result, error, id) => [{ type: "cmoDetail", id }],
    }),
    getCoManufacturingOrderHistory: builder.query({
      query: ({ id, limit, offset }) => ({
        url: `/api/production/v1/co_manufacturing_order/${id}/history/`,
        params: { limit, offset },
      }),
    }),
    getCoManufacturingOrderLines: builder.query({
      query: params => {
        const queryParams =
          typeof params === "string" ? params : new URLSearchParams(params).toString();
        return `/api/production/v1/co_manufacturing_order_line/?${queryParams}`;
      },
    }),
    getCoManufacturingOrderServices: builder.query({
      query: params => {
        const queryParams =
          typeof params === "string" ? params : new URLSearchParams(params).toString();
        return `/api/production/v1/co_manufacturing_order_service/?${queryParams}`;
      },
    }),
  }),
});

export const {
  useCreateManufacturingOrderMutation,
  useUpdateManufacturingOrderMutation,
  usePatchManufacturingOrderMutation,
  useReleaseManufacturingOrderMutation,
  useExpandBomMutation,
  useExpandBomStepsMutation,
  useGetManufacturingOrderQuery,
  useGetManufacturingOrderSelectionQuery,
  useLazyGetManufacturingOrderSelectionQuery,
  useDeleteManufacturingOrderMutation,
  useGetManufacturingOrderDetailQuery,
  useGetManufacturingOrderHistoryQuery,
  useGetMoStepsQuery,
  useLazyGetMoStepsQuery,
  useGetMoComponentsQuery,
  useLazyGetMoComponentsQuery,
  useGetMoLaboursQuery,
  useLazyGetMoLaboursQuery,
  useGetMoSourcesQuery,
  useLazyGetMoSourcesQuery,
  useCreateCoManufacturingOrderMutation,
  useUpdateCoManufacturingOrderMutation,
  usePatchCoManufacturingOrderMutation,
  useSubmitCoManufacturingOrderMutation,
  useDeleteCoManufacturingOrderMutation,
  useGetCoManufacturingOrderQuery,
  useLazyGetCoManufacturingOrderQuery,
  useGetCoManufacturingOrderSelectionQuery,
  useLazyGetCoManufacturingOrderSelectionQuery,
  useGetCoManufacturingOrderDetailQuery,
  useLazyGetCoManufacturingOrderDetailQuery,
  useGetCoManufacturingOrderHistoryQuery,
  useLazyGetCoManufacturingOrderHistoryQuery,
  useGetCoManufacturingOrderLinesQuery,
  useLazyGetCoManufacturingOrderLinesQuery,
  useGetCoManufacturingOrderServicesQuery,
  useLazyGetCoManufacturingOrderServicesQuery,
} = manufacturingOrderApiSlice;

